// Waveform.tsx — a real waveform drawn from decoded audio peaks, with an
// optional draggable start/end region and a playhead. Used in two places on the
// Studio page: the loaded-track view (interactive region selection over the
// uploaded file) and the result console (static bars for the isolated stem with
// a moving playhead). Ported from the Claude Design handoff and wired to real
// PCM peaks instead of the prototype's seeded pseudo-waveform.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface Region {
  start: number;
  end: number;
}

// Downsample decoded stereo PCM to `bars` normalized peak heights (0.06..1).
export function computePeaks(left: Float32Array, right: Float32Array, bars: number): number[] {
  const n = Math.max(1, Math.min(left.length, right.length));
  const bucket = Math.max(1, Math.floor(n / bars));
  const out: number[] = [];
  let max = 0;
  for (let b = 0; b < bars; b++) {
    const i0 = b * bucket;
    const i1 = Math.min(n, i0 + bucket);
    let peak = 0;
    for (let i = i0; i < i1; i++) {
      const v = Math.abs((left[i] + right[i]) * 0.5);
      if (v > peak) peak = v;
    }
    out.push(peak);
    if (peak > max) max = peak;
  }
  const norm = max > 0 ? 1 / max : 1;
  // gamma < 1 lifts quiet passages so the wave reads as a wave, not a flat line
  return out.map((v) => Math.max(0.06, Math.min(1, Math.pow(v * norm, 0.7))));
}

/** Signed min/max envelope, interleaved [min0, max0, min1, max1, …].
 *
 * This is the Moises-style lane trace: where `computePeaks` folds the two
 * channels to mono and keeps one |peak| per bucket, this keeps the true
 * negative and positive extremes across BOTH channels — mono-summing cancels
 * out-of-phase material, and a single magnitude loses the top/bottom asymmetry
 * that makes an envelope read as audio rather than a bar chart.
 *
 * Values are normalised to the clip's own loudest excursion with a mild gamma
 * on the magnitude (sign preserved) so quiet passages stay visible. There is
 * deliberately NO floor: digital silence comes out as exactly 0/0 and draws
 * nothing, which is what keeps silent stretches clean instead of the old
 * "thick line through the rests".
 */
export function computeEnvelope(left: Float32Array, right: Float32Array, buckets: number): Float32Array {
  const n = Math.max(1, Math.min(left.length, right.length));
  const bucket = Math.max(1, Math.floor(n / buckets));
  const out = new Float32Array(buckets * 2);
  let peak = 0;
  for (let b = 0; b < buckets; b++) {
    const i0 = b * bucket;
    const i1 = Math.min(n, i0 + bucket);
    let mn = 0, mx = 0;
    for (let i = i0; i < i1; i++) {
      const l = left[i], r = right[i];
      const lo = l < r ? l : r, hi = l > r ? l : r;
      if (lo < mn) mn = lo;
      if (hi > mx) mx = hi;
    }
    out[b * 2] = mn;
    out[b * 2 + 1] = mx;
    if (-mn > peak) peak = -mn;
    if (mx > peak) peak = mx;
  }
  if (peak > 0) {
    const inv = 1 / peak;
    for (let i = 0; i < out.length; i++) {
      const v = out[i] * inv;
      out[i] = Math.sign(v) * Math.pow(Math.abs(v), 0.75);
    }
  }
  return out;
}

// Deterministic mini-bars for saved sections (no decoded audio on hand).
export function seededBars(n: number, seed = 7): number[] {
  let s = seed >>> 0;
  const rnd = () => {
    s = (s * 1664525 + 1013904223) % 4294967296;
    return s / 4294967296;
  };
  const bars: number[] = [];
  for (let i = 0; i < n; i++) {
    const t = i / n;
    const env = 0.42 + 0.34 * Math.sin(t * Math.PI * 3.2) + 0.2 * Math.sin(t * Math.PI * 9) + 0.12 * Math.sin(t * Math.PI * 23);
    bars.push(Math.max(0.08, Math.min(1, (0.55 + env * 0.5) * (0.55 + rnd() * 0.65))));
  }
  return bars;
}

function fmt(sec: number) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

interface Props {
  peaks: number[];
  duration: number;
  region: Region;
  /** Provide to make the region draggable; omit for a static (result) wave. */
  onRegion?: (r: Region) => void;
  /** Provide to play from a point on double-click (passes the clicked time). */
  onSeekPlay?: (time: number) => void;
  accent: string;
  playing?: boolean;
  /** Absolute playhead position in seconds (0..duration). */
  playPos?: number;
  height?: number;
  axis?: boolean;
  /** Read-only labelled bands (e.g. auto-detected "Guitar solo" regions). */
  bands?: { startSec: number; endSec: number; color: string; label: string }[];
  /**
   * Draw the region handles as labelled A / B pills instead of plain bars.
   * Opt-in so the live Studio's handles are untouched.
   */
  lettered?: boolean;
  /**
   * First-run call-out floated over the untouched region — the extension's
   * `.cr-region-hint`. Disappears for good the moment A or B actually moves,
   * so it teaches once and never nags. `lettered` only; ignored without it,
   * because the sentence names caps that the plain handles don't have.
   */
  dragHint?: string;
  /**
   * Shown on the region itself when a plan limit is HOLDING one of its edges,
   * e.g. "60s max". The numbers row below already flags it, but the hand is up
   * here during a drag — without this the box simply stops growing, which
   * reads as a broken handle rather than a limit. Null when nothing is capped.
   */
  capLabel?: string | null;
  /**
   * Makes `capLabel` clickable — the badge becomes the offer itself, so the
   * limit can be lifted from the exact spot it bites without another
   * unprompted modal. Omit and the badge stays purely informational.
   */
  onCapClick?: () => void;
  /**
   * While true the badge re-runs its pulse — set for a few seconds after each
   * refused drag. The badge itself stays as long as `capLabel` is set; this
   * only draws the eye back to it when the region refuses to grow again.
   */
  capPulse?: boolean;
}

export function Waveform({ peaks, duration, region, onRegion, onSeekPlay, accent, playing, playPos = 0, height = 116, axis = true, bands, lettered = false, dragHint, capLabel = null, onCapClick, capPulse = false }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef<{ which: 'l' | 'r' | 'move'; originX: number; s0: number; e0: number } | null>(null);
  // Keep latest region/duration readable inside window-level drag handlers.
  const regionRef = useRef(region);
  regionRef.current = region;
  const durRef = useRef(duration);
  durRef.current = duration;

  // The call-out's gate. Deliberately NOT "has the region changed" — the parent
  // also writes `region` from the A/B time inputs, the "Jump to" suggestion
  // chips and its own centre-on-load clamp, and none of those teach you that
  // the caps drag. Only a real gesture on this waveform clears it, which is the
  // same narrow rule the extension had to retrofit after `widgetTouched` turned
  // out to fire for things that were not touching the section.
  const [ranged, setRanged] = useState(false);
  // A new track earns another run of the lesson. Keyed on a FINGERPRINT of the
  // audio, not on the `peaks` array's identity: the link tab derives its bars
  // from `linkMeta`, and that object is replaced once when the YouTube metadata
  // fetch resolves — same track, new array — which would otherwise re-show a
  // hint the user had already dismissed. The bars are seeded from the video ref
  // so the same track always yields the same VALUES, making this stable.
  const sig = `${peaks.length}:${duration.toFixed(2)}:${peaks[0] ?? 0}:${peaks[peaks.length - 1] ?? 0}`;
  useEffect(() => { setRanged(false); }, [sig]);
  // Half the pill's rendered width, so it can be clamped inside the box below.
  // Measured rather than hardcoded: the copy is goal-specific, so the width is
  // not a constant we could keep in sync by hand.
  const hintRef = useRef<HTMLDivElement>(null);
  const [hintHalf, setHintHalf] = useState(102);
  useLayoutEffect(() => {
    const el = hintRef.current;
    if (el && el.offsetWidth) setHintHalf(el.offsetWidth / 2);
  }, [dragHint, ranged]);

  const interactive = !!onRegion;
  const barCount = Math.max(1, peaks.length);
  const w = 1000;
  const h = 100;
  const bw = w / barCount;
  const dur = Math.max(0.001, duration);
  // Clamp to [0,1] so a region/playhead that runs past the duration can never
  // paint outside the waveform bars.
  const clamp01 = (n: number) => Math.max(0, Math.min(1, n));
  const fStart = clamp01(region.start / dur);
  const fEnd = clamp01(region.end / dur);

  const pxToTime = (clientX: number) => {
    const el = ref.current;
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    const f = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    return f * durRef.current;
  };

  const onMove = (e: PointerEvent) => {
    const d = drag.current;
    if (!d || !onRegion) return;
    const t = pxToTime(e.clientX);
    const reg = regionRef.current;
    if (d.which === 'l') onRegion({ start: Math.min(t, reg.end - 1), end: reg.end });
    else if (d.which === 'r') onRegion({ start: reg.start, end: Math.max(t, reg.start + 1) });
    else {
      const el = ref.current!.getBoundingClientRect();
      const dt = ((e.clientX - d.originX) / el.width) * durRef.current;
      let ns = d.s0 + dt;
      let ne = d.e0 + dt;
      const len = d.e0 - d.s0;
      if (ns < 0) {
        ns = 0;
        ne = len;
      }
      if (ne > durRef.current) {
        ne = durRef.current;
        ns = durRef.current - len;
      }
      onRegion({ start: ns, end: ne });
    }
  };

  const endDrag = () => {
    drag.current = null;
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', endDrag);
  };

  const startDrag = (which: 'l' | 'r' | 'move') => (e: React.PointerEvent) => {
    if (!onRegion) return;
    e.preventDefault();
    e.stopPropagation();
    const reg = regionRef.current;
    // Every gesture that can reach here moves A, B or the whole window — the
    // three things the call-out is teaching. Set it on pointerDOWN, not on the
    // first move: a press on a cap is already proof the user found it.
    setRanged(true);
    drag.current = { which, originX: e.clientX, s0: reg.start, e0: reg.end };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', endDrag);
  };

  // Double-click anywhere on the waveform => play from that point to the end.
  const onDouble = (e: React.MouseEvent) => {
    if (!onSeekPlay) return;
    onSeekPlay(pxToTime(e.clientX));
  };

  return (
    <div className="st-wave-wrap" style={{ ['--st-wa' as string]: accent }}>
      <div
        className={'st-wave-canvas' + (interactive ? '' : ' static')}
        ref={ref}
        onDoubleClick={onSeekPlay ? onDouble : undefined}
        title={
          onSeekPlay
            ? interactive
              // Same reason the hints above the picker name A and B: "the
              // handles" points at nothing. Only the lettered variant has
              // letters to point at — the live Studio keeps its plain wording.
              ? `Double-click to play from here · drag ${lettered ? 'A and B' : 'the handles'} to select`
              : 'Double-click to play from here'
            : undefined
        }
        style={{ height, ...(onSeekPlay && !interactive ? { cursor: 'pointer' } : {}) }}
      >
        <svg className="st-wave-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
          {peaks.map((v, i) => {
            const x = i * bw;
            const bh = v * (h - 8);
            const f = (i + 0.5) / barCount;
            const inside = f >= fStart && f <= fEnd;
            return (
              <rect
                key={i}
                x={x + bw * 0.13}
                y={(h - bh) / 2}
                width={bw * 0.74}
                height={bh}
                rx={Math.min(2.5, bw * 0.37)}
                fill={inside ? accent : 'currentColor'}
                style={{ color: 'var(--st-text-faint)', opacity: inside ? 0.95 : 0.4 }}
              />
            );
          })}
        </svg>

        {bands?.map((b, i) => {
          const l = clamp01(b.startSec / dur);
          const w = clamp01(b.endSec / dur) - l;
          if (w <= 0) return null;
          return (
            <div
              key={i}
              className="st-wave-band"
              style={{ left: `${l * 100}%`, width: `${w * 100}%`, ['--st-band' as string]: b.color }}
              title={b.label}
            >
              <span className="st-wave-band-tag">{b.label}</span>
            </div>
          );
        })}

        {/* Floated over the untouched region, at the TOP of the box so the
            region outline and both caps stay readable below it. pointer-events
            is none — it must never eat a drag aimed at the wave beneath it,
            which is the one gesture it is asking for. Clamped inside the box by
            its own measured half-width so it stays whole at either end of the
            song. */}
        {interactive && lettered && dragHint && !ranged && (
          <div
            ref={hintRef}
            className="st-region-hint"
            style={{
              ['--hint-half' as string]: `${hintHalf}px`,
              ['--hint-c' as string]: `${((fStart + fEnd) / 2) * 100}%`,
            }}
            aria-hidden="true"
          >
            {dragHint.split(/\b([AB])\b/).map((part, i) => (
              part === 'A' || part === 'B' ? <b key={i}>{part}</b> : part
            ))}
          </div>
        )}

        {interactive && (
          <div
            className={'st-region' + (lettered ? ' lettered' : '') + (capLabel ? ' capped' : '')}
            style={{ left: `${fStart * 100}%`, width: `${(fEnd - fStart) * 100}%`, cursor: 'grab' }}
            onPointerDown={startDrag('move')}
          >
            {capLabel && (onCapClick
              ? (
                <button
                  type="button"
                  className={'st-region-cap is-btn' + (capPulse ? ' is-pulse' : '')}
                  // The region drags on pointerdown; without this, aiming at the
                  // badge would grab the region instead of pressing the button.
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); onCapClick(); }}
                >
                  {capLabel}
                </button>
              )
              : <span className="st-region-cap">{capLabel}</span>)}
            <div className="st-region-handle l" onPointerDown={startDrag('l')} aria-label="Section start">
              {lettered && <span className="st-region-let">A</span>}
            </div>
            <div className="st-region-handle r" onPointerDown={startDrag('r')} aria-label="Section end">
              {lettered && <span className="st-region-let">B</span>}
            </div>
          </div>
        )}

        <div className={'st-playhead' + (playing ? ' on' : '')} style={{ left: `${clamp01(playPos / dur) * 100}%` }} />
      </div>
      {axis && (
        <div className="st-wave-axis st-mono">
          <span>0:00</span>
          <span>{fmt(dur * 0.25)}</span>
          <span>{fmt(dur * 0.5)}</span>
          <span>{fmt(dur * 0.75)}</span>
          <span>{fmt(dur)}</span>
        </div>
      )}
    </div>
  );
}
